import template from 'modal/modal.j2';

import { debug, error } from '../utils/logger';

import {
    CLOSE_SITE_NAV
} from '../actions/headerActions.js';

import { generate } from '../../../core/js/utils/componentUtils';
import { init as modalInit } from './modalComponent';
import { analyticsAction } from '../actions/analyticsActions';
import { trackOriginComponent } from '../actions/modalActions';
import { modalHashLauncherSaga } from '../../../core/js/sagas/globalModalHashLauncher';

import { detachSwiperEvents, attachSwiperEvents } from '../../../product/js/utils/swiperUtils';

function afterConstruction(store) {
    // Don't init the hashlauncher for pwa_integration
    if (!this.ignoreHashes) {
        store.runSaga(modalHashLauncherSaga);
    }
}

function wireOverrides(payload, modal) {
    if (modal.uuid) {
        payload.overrides.templateLocation = `.${modal.uuid} ${modal.contentsLocation}`;
    } else {
        // External modals erase the UUID
        payload.overrides.templateLocation = `${modal.contentsLocation}`;
    }

    // Allow the the close method to be overriden...for... whatever reason
    // Do we really want to allow this?
    if (!payload.overrides.closeAction) {
        payload.overrides.closeAction = (store) => {
            modal.closeAction(store);
        };
    }
}

function getModalOutcome(modal) {
    const modalLocation = `${modal.componentLocation} ${modal.contentsLocation}`;
    const componentOutcome = _.get(modal, `childrenObjects['${modalLocation}'].modalOutcome`);

    // Always take the outcome on the current modal. If no outcome exists, fall
    // back to any outcomes that may be on the singleton. Some modals, such as
    // quickshop must use the singleton due to deeply nested children not having
    // a great way to communicate component outcomes to the parent.
    return componentOutcome ? componentOutcome : modalShepherdSingleton.outcome;
}

/**
 * Attach component events to DOM
 * @param {Object} store Redux store
 * @returns {undefined}
 */
function attachEvents(store) {
    /**
     * This will be eventually replaced with a Redux channel
     *  but that requires upgrading redux and doing more refactoring
     */
    $(document).on('PUSH_MODAL_TRANSITION', (e) => {
        debug('Modal Shepherd: push modal');
        const payload = e.payload.payload;
        const isFirstModalOpened = this.stack.length === 0;
        setTimeout(() => {
            store.dispatch(analyticsAction(payload.meta.event, payload.meta.componentName));
            store.dispatch(trackOriginComponent(payload.meta.componentName));
        }, 0);

        if (isFirstModalOpened) {
            this.beforeFirstOpened(payload.meta.event);
        }

        const defaultModalInit = modalInit;

        const uuid = `js-uuid-${Date.now()}`;
        const modalOverrides = {
            uuid: uuid,
            templateLocation: `.${uuid}`
        };

        // Create a new modal container, insert it into the dom under the
        //  modals-anchor div
        const child = this.addChild([payload.overrides.modalInit || defaultModalInit, modalOverrides], store);

        // External modals erase the uuid, so looking up by the uuid/templateLocation won't work in that
        //  case.  Modals should have one and only one contents component.  So index 0 should be good
        const modal = child[Object.keys(child)[0]];

        wireOverrides(payload, modal);

        const modalChildComponent = modal.addChild([payload.init, payload.overrides], store);

        if (_.isEmpty(modalChildComponent)) {
            // Modal component determined it was not a valid component.  Abort the modal
            //  push flow
            debug('Modal Shepherd: aborting modal');
            modal.destroyComponent(store, e);
            return;
        }

        if (!isFirstModalOpened) {
            // Hide previous modal
            this.stack[this.stack.length - 1].hide();
        } else {
            // First modal opened.  Do stuff if needed
            this.firstOpened(payload.meta.event);
        }
        this.stack.push(modal);
    });

    /**
     * This is one way of closing the active modal
     *
     * Normally one would call modal.closeAction, click the cancel button, click the X, etc
     *
     * However, there are times in sagas where you might not have a reference to the active modal
     *  but would like to close it.  This is an appropriate time to call this transition directly
     *
     * NOTE: This transition does *not* call the cancel flow.  It only calls the close flow
     */
    $(document).on('POP_MODAL_TRANSITION', (e) => {
        debug('Modal Shepherd: popping modal');

        const payload = e.payload.payload;
        store.dispatch(analyticsAction(payload.meta.event, payload.meta.componentName));

        const modal = this.stack.pop();
        const outcome = getModalOutcome(modal);

        if (modal) {
            modal.hide(store);
            modal.destroyComponent(store, e);
        }

        if (this.stack.length > 0) {
            this.stack[this.stack.length - 1].reshow();
        } else {
            this.lastClosed(outcome);
            // Remove current stored outcome
            this.outcome = null;
        }
    });

    /**
     * This pops ALL modals off the stack.
     *
     * The cancel & close flow is called
     */
    $(document).on('POP_ALL_MODAL_TRANSITION', (e) => {
        let modal = this.stack.pop();

        if (modal) {
            const outcome = getModalOutcome(modal);

            while (modal) {
                modal.cancelAction(store, e);
                modal.hide(store);
                modal.destroyComponent(store, e);
                modal = this.stack.pop();
            }

            this.lastClosed(outcome);
            //  Remove current stored outcome
            this.outcome = null;
        }
    });

    $(document).on('INNER_PUSH_MODAL_TRANSITION', (e) => {
        const payload = e.payload.payload;

        store.dispatch(analyticsAction(payload.meta.event, payload.meta.componentName));
    });

    $(document).on('INNER_POP_MODAL_TRANSITION', (e) => {
        const payload = e.payload.payload;

        store.dispatch(analyticsAction(payload.meta.event, payload.meta.componentName));
    });

    $(document).on('REPLACE_MODAL_TRANSITION', (e) => {
        const payload = e.payload.payload;

        store.dispatch(analyticsAction(payload.meta.event, payload.meta.componentName));

        if (this.stack.length <= 0) {
            error('Trying to replace a non-existent modal.');
            return;
        }

        // Find the top-most modal
        const modal = this.stack[this.stack.length - 1];

        modal.removeAllChildren();

        wireOverrides(payload, modal);

        modal.addChild([payload.init, payload.overrides], store);
    });
}

/**
 * Bootstrap the component onto the page
 * @param {Object} store Redux store
 * @returns {undefined}
 */
export function initialize(store) {
    attachEvents(store);
}

/**
 * Hook for before the first modal is pushed onto the stack.
 * @param {Object} originatingEvent UNUSED JQuery event that triggered the modal
 */
function beforeFirstOpened(originatingEvent) {
    detachSwiperEvents();
}

/**
 * Hook for when the first modal is pushed onto the stack.
 * Do all initial startup actions here
 * @param {Object} originatingEvent JQuery event that triggered the modal
 */
function firstOpened(originatingEvent) {
    /* store off the active element so that it can be
     * reselected once all the modals are closed.
     *
     * This *doesn't* seem to work correctly if a modal is popped up from
     *  a <div> or <img> link... So, for those cases the event should
     *  be passed through the meta
     */
    this.originatingElement = _.get(originatingEvent, 'target') || document.activeElement;
    this.originatingScrollOffset = $(window).scrollTop();

    // prevent body from scrolling behind modal
    $(document.body).addClass(this.bodyClass);

    $.event.trigger({
        type: CLOSE_SITE_NAV
    });
}

/**
 * Hook for when the last modal is popped off the stack.
 */
function lastClosed(outcome) {
    // restore scrollable body
    $(document.body).removeClass(this.bodyClass);
    $('.js-site-wrap').attr('aria-hidden', 'false');

    // Reselect the element that popped up the first modal
    $(this.originatingElement).focus();

    attachSwiperEvents();

    // Scroll back to the original scroll location
    $('html, body').animate({
        scrollTop: this.originatingScrollOffset
    }, 100);

    // Fire an event for PWA
    document.dispatchEvent(new CustomEvent('a15-modal-closed', {
        detail: outcome
    }));
}

export let modalShepherdSingleton;

export function init(store, overrides = {}) {
    const config = {
        templateLocation: 'dom-modals-anchor',
        attachEvents: attachEvents,
        template: template,
        name: 'modal.shepherd',
        replace: false,
        bodyClass: 'has-modal-open',
        stack: [],
        afterConstruction: afterConstruction,
        beforeFirstOpened: beforeFirstOpened,
        firstOpened: firstOpened,
        lastClosed: lastClosed,
        ignoreHashes: overrides.feature === 'pwa',
    };

    const componentInstance = generate(config, store, overrides);

    if (!modalShepherdSingleton) {
        modalShepherdSingleton = componentInstance['dom-modals-anchor'];
    }

    // When we're in the PWA feature (via pwa_integration), it wires up it's
    // own versions of these events because it needs to do more than just init
    // the modals (i.e., run initial sagas, etc.).
    //
    // When we're running in MonoWeb or UCC, however, all the store/sagas initialization
    // will have already happened (via the feature we're on), but we need to initialize
    // the modal listeners.  So we wire up the same event bus here to only initialize
    // the modals
    if (overrides.feature !== 'pwa') {
        document.addEventListener('a15-pwa-init', function onA15PwaInit() {
            document.removeEventListener('a15-pwa-init', onA15PwaInit);
            import('@pwa_integration/js/modals').then((pwaModals) => {
                pwaModals.init(store);
                document.dispatchEvent(new CustomEvent('a15-pwa-ready'));
            });
        });
    }

    return componentInstance;
}
